// Receipts for guided onboarding. The owner's 2026-10-17 complaint was that a
// confirmation appeared at the top of the page, out of sight. A receipt here
// belongs inside the card of the action that produced it: appended under the
// button, focused, and scrolled into view. Never a top-of-page banner.
//
// Everything rendered is plain text (dom.ts h() only). A one-time secret is
// held by the node's own copy-button closure only — never in page state, the
// URL, storage, a log line, or a fixture.
//
// rd round 12, lane A5a, item A5-3 (round-11 final report item 27, HIGH). The
// whole card used to be `role="status" aria-live="polite"`, so a screen reader
// read a one-time API key, invitation link, approval secret or agent bearer
// aloud, in one of the few places the value is ever shown. The live region is
// now the happened/unchanged/next lines only; the one-time block sits beside
// it, outside every live region, and is not announced. The card still takes
// focus and is still scrolled into view (the announcement is not lost), and
// `aria-describedby` names the happened line so focus still says what
// happened (a live region inserted with its content is not reliably
// announced).

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { h } from "../dom.js";
import type { OnboardingReceipt } from "../types.js";

export interface ReceiptCardOptions {
  /**
   * A value shown once under the receipt (API key, invitation URL). The string
   * is never written to an attribute, only to a text node, and never inside the
   * live region.
   */
  oneTime?: { label: string; value: string };
  /** Optional data-role suffix so tests can tell two one-time values apart. */
  oneTimeRole?: string;
  /**
   * Portal-built words (lane A4's `face.receiptWords`), so a page can stop
   * rendering the Worker's sentence verbatim. `next: null` omits the line.
   * `details` is the Worker's own sentence (it may quote a slug or an id) and
   * is rendered ONLY inside "Show details", never on the face.
   */
  words?: { happened: string; unchanged: string; next: string | null; details?: string };
}

/**
 * Append the receipt, move focus to it, and bring it into view.
 *
 * The checklist builds a card (and appends its receipt) before the card is
 * attached to the page. `scrollIntoView` and `focus()` are no-ops on a detached
 * node, which is exactly how a receipt can render off-screen in a real browser
 * while a happy-dom test that mounts the host first still passes. When the
 * receipt is not yet connected, defer the reveal to the microtask that runs
 * after the page assembly finishes.
 */
export function appendReceipt(
  host: HTMLElement,
  receipt: HTMLElement,
): void {
  host.append(receipt);
  if (receipt.isConnected) {
    focusAndReveal(receipt);
    return;
  }
  queueMicrotask(() => {
    if (receipt.isConnected) focusAndReveal(receipt);
  });
}

/** Focus a receipt and scroll it into view (happy-dom has no layout). */
export function focusAndReveal(receipt: HTMLElement): void {
  receipt.focus();
  const target = receipt as HTMLElement & {
    scrollIntoView?: (options?: ScrollIntoViewOptions) => void;
  };
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ block: "center", inline: "nearest" });
  }
}

/** Ids are referenced by `aria-describedby`; one per rendered card. */
let receiptSerial = 0;

function nextHappenedId(): string {
  receiptSerial += 1;
  return `receipt-happened-${String(receiptSerial)}`;
}

/** A successful action's receipt, rendered inside the acting card. */
export function renderReceiptCard(
  receipt: OnboardingReceipt,
  options: ReceiptCardOptions = {},
): HTMLElement {
  const happenedId = nextHappenedId();
  const happened = options.words?.happened ?? receipt.happened;
  const unchanged = options.words?.unchanged ?? receipt.unchanged;
  // `face.receiptWords` builds the whole next line, "Next: …" included (one
  // owner for the words), so it is shown as given; prefixing it again read
  // "Next: Next: …" (rd r12 F1, R7).
  const nextLine =
    options.words !== undefined
      ? options.words.next === null
        ? null
        : amVisibleDisplayText(options.words.next)
      : receipt.next === null
        ? null
        : `Next: ${amVisibleDisplayText(receipt.next.who)} — ${amVisibleDisplayText(receipt.next.text)}`;

  // The card takes focus, and a focusable region with no accessible name is
  // read out in full by some screen readers — a one-time secret included. The
  // name says what was focused; `aria-describedby` points at the happened line
  // so focus still announces what happened (round-2 N5: a live region inserted
  // together with its content is not reliably announced). The description is
  // the happened line only, never the one-time block.
  const card = h(
    "section",
    {
      class: "receipt-card banner-ok",
      "data-role": "receipt",
      role: "group",
      "aria-label": "Receipt",
      "aria-describedby": happenedId,
      tabindex: "-1",
    },
    [
      h(
        "div",
        {
          role: "status",
          "aria-live": "polite",
          "data-role": "receipt-announcement",
        },
        [
          h(
            "p",
            { class: "receipt-happened", "data-role": "receipt-happened", id: happenedId },
            [
              h("span", { "aria-hidden": "true" }, ["✔ "]),
              // The receipt's sentences come from the Worker and quote the company
              // name the staff entered: clean them like any other API text.
              amVisibleDisplayText(happened),
            ],
          ),
          h("p", { class: "receipt-unchanged muted", "data-role": "receipt-unchanged" }, [
            amVisibleDisplayText(unchanged),
          ]),
          nextLine === null
            ? null
            : h("p", { class: "receipt-next", "data-role": "receipt-next" }, [nextLine]),
        ],
      ),
    ],
  );

  if (options.words?.details !== undefined && options.words.details.length > 0) {
    card.append(
      h("details", { class: "disclosure", "data-role": "receipt-details" }, [
        h("summary", {}, ["Show details"]),
        h("p", {}, [amVisibleDisplayText(options.words.details)]),
      ]),
    );
  }

  if (options.oneTime !== undefined) {
    const value = options.oneTime.value;
    // Its own live region: "Copied." is still announced, while the one-time
    // block beside it stays unannounced. The span never holds the value.
    const copied = h("span", {
      class: "muted",
      role: "status",
      "data-role": "receipt-copy-status",
    });
    const copy = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "copy-one-time",
      },
      ["Copy"],
    );
    copy.addEventListener("click", () => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(value);
          copied.textContent = "Copied.";
        } catch {
          copied.textContent = "Could not copy — select the text and copy it.";
        }
      })();
    });
    // Deliberately OUTSIDE the live region: a one-time secret must never be
    // read aloud by a screen reader.
    card.append(
      h("p", { class: "receipt-one-time-label", "data-role": "receipt-one-time-label" }, [
        options.oneTime.label,
      ]),
      h("div", { class: "receipt-one-time-row" }, [
        h(
          "code",
          {
            class: "receipt-one-time",
            "data-role": options.oneTimeRole ?? "receipt-one-time",
          },
          [value],
        ),
        copy,
        copied,
      ]),
      h("p", { class: "muted" }, ["Shown once. It is not stored on this page."]),
    );
  }

  return card;
}

/**
 * A failed action's receipt — same place, same shape, plain words.
 *
 * `unchanged` is REQUIRED (rd round 12, B3b-2). "Nothing changed." was the
 * default, and it is false wherever the Worker may already have acted (an agent
 * revoke or create, an approval approve or reveal, every checklist action). Each
 * caller now states what it actually knows: `outcomeSentence(outcome, role)` for
 * a `runWrite` outcome, or the page's own honest line for a read it performed
 * itself. Only a refusal that sent nothing may read "Nothing changed."
 */
export function renderReceiptFailure(message: string, unchanged: string): HTMLElement {
  return h(
    "section",
    {
      class: "receipt-card banner-err",
      role: "alert",
      "data-role": "receipt",
      "data-status": "failed",
      // Focus lands here too; the name keeps a screen reader from reading the
      // whole card out on focus.
      "aria-label": "Receipt",
      tabindex: "-1",
    },
    [
      h("p", { class: "receipt-happened", "data-role": "receipt-happened" }, [
        h("span", { "aria-hidden": "true" }, ["✖ "]),
        amVisibleDisplayText(message),
      ]),
      h("p", { class: "receipt-unchanged muted", "data-role": "receipt-unchanged" }, [
        unchanged,
      ]),
    ],
  );
}
